<template>
  <div class="suivi-rfa-container">
    <!-- Tuiles de statut -->
    <div class="status-tiles">
      <div class="white-box status-tile">
        <div class="tile-label">A faire</div>
        <div class="tile-value">{{ statusCounts[0] }}</div>
      </div>
      <div class="white-box status-tile">
        <div class="tile-label">Facturé</div>
        <div class="tile-value">{{ statusCounts[1] }}</div>
      </div>
      <div class="white-box status-tile">
        <div class="tile-label">Payé</div>
        <div class="tile-value">{{ statusCounts[2] }}</div>
      </div>
      <div class="white-box status-tile">
        <div class="tile-label">Versé</div>
        <div class="tile-value">{{ statusCounts[3] }}</div>
      </div>
    </div>

    <!-- Suivi des RFA -->
    <div class="white-box suivi-rfa-box">
      <div class="header-section">
      <div class="title-with-mode">
        <h4>Suivi des RFA</h4>
        <select v-model="selectedMode" class="mode-select">
          <option value="annuelle">Annuelle {{ year - 1 }}</option>
          <option value="acomptes">Acomptes {{ effectiveAdvanceYear }}</option>
        </select>
      </div>

      <!-- Options selon le mode sélectionné -->
      <div class="secondary-filter" v-if="selectedMode">
        <!-- Pour l'annuelle: choix du concept (1, 2, 4) -->
        <select v-if="selectedMode === 'annuelle'" v-model="selectedConcept" @change="initData" class="concept-select">
          <option :value="1">CMEM</option>
          <option :value="2">MME</option>
          <option :value="4">WA</option>
        </select>

        <!-- Pour les acomptes: choix de la période -->
        <select v-else v-model="selectedPeriodId" @change="initData" class="period-select">
          <option
            v-for="p of acomptesPeriods"
            :value="p.id"
            :key="p.id"
          >
            {{ p.label }} {{ p.year }}
          </option>
        </select>

        <button @click="handleExport" class="btn-cmem btn-export" :disabled="!effectiveRows || effectiveRows.length === 0">
          Export Excel
        </button>
      </div>
    </div>

    <!-- Zone pour afficher les données -->
    <div v-if="loading" class="loading">Chargement...</div>
    <div v-else class="data-table custom-responsive-suivi-rfa">
      <vue-good-table
        :columns="tableColumns"
        :rows="filteredData"
        ref="table"
        @on-column-filter="updateEffectiveRows"
        :group-options="{
          enabled: true,
          collapsable: false
        }"
        :pagination-options="{
          enabled: false,
          perPage: 50,
          perPageDropdown: [25, 50, 100]
        }"
        :search-options="{
          enabled: false
        }"
      >
        <template #column-filter="{ column }">
          <template v-if="column.field === 'MinStatusText'">
            <MultiSelectFilter
              :options="uniqueMinStatusText"
              :placeholder="'Filtrer par statut global'"
              @update="updateColumnFilters( 'MinStatusText', $event)"
            />
          </template>
          <template v-else-if="column.field === 'BarStatuses'">
            <MultiSelectFilter
              :options="statusOptions"
              :placeholder="'Filtrer par statut'"
              @update="updateStatusFilter"
            />
          </template>
          <template v-else-if="column.field === 'BarRfaTypes'">
            <MultiSelectFilter
              :options="typeOptions"
              :placeholder="'Filtrer par type'"
              @update="updateTypeFilter"
            />
          </template>
        </template>
        <template #table-row="props">
          <span v-if="props.column.field === 'actions'">
            <button @click="showDetails(props.row)" class="btn-details">
              Détails
            </button>
          </span>
          <span v-else-if="props.column.field === 'BarRfaTot'">
            {{ formatCurrency(props.row.BarRfaTot) }}
          </span>
          <span v-else-if="props.column.field === 'BarRfa'">
            {{ formatCurrency(props.row.BarRfa) }}
          </span>
          <span v-else-if="props.column.field === 'TxRfaFixeN1'">
            <span v-if="props.row.TxRfaFixeN1 || props.row.ProgTxRfaFixeN1">
              {{ Math.round(props.row.TxRfaFixeN1 * 10000) / 100 }} %
              <div v-if="Math.round(props.row.ProgTxRfaFixeN1 * 10000)" class="trend-indicator"
                   :class="props.row.ProgTxRfaFixeN1 >= 0 ? 'up' : 'down'">
                {{ Math.round(props.row.ProgTxRfaFixeN1 * 10000) / 100 }}
              </div>
              <div v-else class="text-muted">
                -
              </div>
            </span>
            <span v-else class="text-muted">N/A</span>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </div>
    </div>

    <!-- Modal pour les détails -->
    <Modal :open="showDetailsModal" title="Détails" @close="closeModal" class="xl-modal">
      <vue-good-table
        :columns="detailColumns"
        :rows="selectedRowDetails"
        :pagination-options="{
          enabled: false,
          perPage: 20
        }"
      >
        <template #table-row="props">
          <span v-if="props.column.field === 'BarRfa'">
            {{ formatCurrency(props.row.BarRfa) }}
          </span>
          <span v-else-if="props.column.field === 'BarCreationDate'">
            {{ formatDate(props.row.BarCreationDate) }}
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </Modal>
  </div>
</template>

<script>
import {getSuiviGrfaDetails} from "@/utils/dashboardGrfa";
import Modal from "@/components/Modal.vue";
import MultiSelectFilter from "@/components/DashboardGRFA/MultiSelectFilter.vue";
import {exportExcel} from "@/utils/exportExcel";
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import Vue from "vue";
import store from "@/store/dashboards";

export default {
  name: "SuiviRfa",
  components: {
    Modal,
    MultiSelectFilter
  },
  props: {
    year: {
      type: Number,
      required: true
    },
    detailedPeriods: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      store: store,

      selectedMode: 'annuelle',
      selectedConcept: 1,
      selectedPeriodId: null,

      loading: false,
      data: null,

      effectiveRows: [],

      showDetailsModal: false,
      selectedRowDetails: [],

      columnFilters: {},
      statusFilter: [],
      statusOptions: [
        {value: 0, label: 'A faire'},
        {value: 1, label: 'Facturé'},
        {value: 2, label: 'Payé'},
        {value: 3, label: 'Versé'}
      ],
      typeFilter: [],
      typeOptions: [
        {value: null, label: '(vide)'},
        {value: 1, label: 'Acompte'},
        {value: 2, label: 'Fin d\'année'},
        {value: 4, label: 'Réclamation'}
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initData();
    });
  },
  computed: {
    storeGlobalView() {
      return this.store.globalView;
    },
    // Période annuelle N-1
    annualPeriod() {
      return this.detailedPeriods.find(p =>
        p.declarationType == 3 &&
        p.year == (this.year - 1)
      );
    },
    effectiveAdvanceYear() {
      let effectiveYear = this.year;
      if (dayjs().year() < this.year)
        effectiveYear -= 1;
      return effectiveYear;
    },
    // Périodes d'acomptes pour l'année N
    acomptesPeriods() {
      return this.detailedPeriods.filter(p =>
        p.declarationType == 2 &&
        [2, 4].includes(p.rfaPaymentFrequency) &&
        p.year == this.effectiveAdvanceYear
      );
    },
    // Vérifier si plusieurs GRFA distincts sont présents dans les données
    hasMultipleGrfa() {
      if (!this.data || !Array.isArray(this.data)) return false;
      const grfas = new Set(this.data.map(row => row.Grfa));
      return grfas.size > 1;
    },
    groupedData() {
      if (!this.data || !Array.isArray(this.data)) return [];

      // Filtrer les données par statut et type AVANT le regroupement
      let filteredRows = this.data;
      if (this.statusFilter && this.statusFilter.length > 0) {
        filteredRows = filteredRows.filter(row => this.statusFilter.includes(row.BarStatus));
      }
      if (this.typeFilter && this.typeFilter.length > 0) {
        filteredRows = filteredRows.filter(row => this.typeFilter.includes(row.BarRfaType));
      }

      const groups = {};

      filteredRows.forEach(row => {
        const key = row.SupplierGuid;

        if (!groups[key]) {
          groups[key] = {
            id: key,
            SupplierGuid: row.SupplierGuid,
            FrsName: row.FrsName,
            FrsIdCompta: row.FrsIdCompta,
            N1_Code: row.N1_Code,
            Grfa: row.Grfa,
            CdmComment: row.CdmComment,
            CountryGathering: row.CountryGathering,
            IsSurFournisseur: row.IsSurFournisseur,
            IsSurFournisseurText: row.IsSurFournisseur ? 'Oui' : 'Non',
            IsVolume: row.IsVolume,
            IsVolumeText: row.IsVolume ? 'Oui' : 'Non',
            TxRfaFixeN1: row.TxRfaFixeN1,
            ProgTxRfaFixeN1: row.ProgTxRfaFixeN1,
            IsLnL: row.IsLnL,
            IsLnLText: row.IsLnL ? 'Oui' : 'Non',
            IsWA: row.IsWA,
            IsWAText: row.IsWA ? 'Oui' : 'Non',
            BarRfa: 0,
            BarRfaTot: 0,
            MinStatus: null,
            MinStatusText: null,
            details: [],
            statusMap: new Map(), // Pour collecter les statuts uniques avec leur ID
            typeMap: new Map() // Pour collecter les types uniques avec leur ID
          };
        }

        if (row.BarCegidId)
          groups[key].details.push(row);
        groups[key].BarRfa += row.BarRfa || 0;
        groups[key].BarRfaTot += row.BarRfaTot || 0;

        // Collecter les statuts uniques
        if (row.BarStatus !== null && row.BarStatus !== undefined && row.BarStatusText) {
          groups[key].statusMap.set(row.BarStatus, row.BarStatusText);
        }

        // Collecter les types uniques
        if (row.BarRfaType !== null && row.BarRfaType !== undefined && row.BarRfaTypeText) {
          groups[key].typeMap.set(row.BarRfaType, row.BarRfaTypeText);
        }

        // Calculer MinStatus uniquement pour BarRfaType == 1, 2 ou 3 (hors reclamation)
        if ([1, 2, 3].includes(row.BarRfaType)) {
          if (groups[key].MinStatus === null || row.BarStatus < groups[key].MinStatus) {
            groups[key].MinStatus = row.BarStatus;
            groups[key].MinStatusText = row.BarStatusText;
          }
        }
      });

      return Object.values(groups).map(g => {
        if (g.MinStatus === null) {
          g.MinStatus = 0;
          g.MinStatusText = "A faire";
        }

        // Générer BarStatuses: trier par ID de statut et concaténer
        const sortedStatuses = Array.from(g.statusMap.entries())
          .sort((a, b) => a[0] - b[0]) // Trier par BarStatus
          .map(entry => entry[1]); // Récupérer les BarStatusText
        g.BarStatuses = sortedStatuses.join(', ');

        // Générer BarRfaTypes: trier par ID de type et concaténer
        const sortedTypes = Array.from(g.typeMap.entries())
          .sort((a, b) => a[0] - b[0]) // Trier par BarRfaType
          .map(entry => entry[1]); // Récupérer les BarRfaTypeText
        g.BarRfaTypes = sortedTypes.join(', ');

        // Supprimer les maps temporaires
        delete g.statusMap;
        delete g.typeMap;

        return g;
      });
    },
    // Options uniques pour les filtres
    uniqueGrfas() {
      if (!this.groupedData) return [];
      return [...new Set(this.groupedData.map(row => row.Grfa))].sort();
    },
    uniqueMinStatusText() {
      if (!this.groupedData) return [];
      return [...new Set(this.groupedData.map(row => row.MinStatusText))].sort();
    },
    uniqueCountryGathering() {
      if (!this.groupedData) return [];
      return [...new Set(this.groupedData.map(row => row.CountryGathering))].filter(Boolean).sort();
    },
    uniqueIsSurFournisseurText() {
      return ['Oui', 'Non'];
    },
    uniqueIsVolumeText() {
      return ['Oui', 'Non'];
    },
    uniqueIsLnLText() {
      return ['Oui', 'Non'];
    },
    uniqueIsWAText() {
      return ['Oui', 'Non'];
    },
    uniqueN1Code() {
      if (!this.groupedData) return [];
      return [...new Set(this.groupedData.map(row => row.N1_Code))].filter(Boolean).sort();
    },
    filteredData() {
      let data = this.groupedData;

      // Appliquer les filtres custom multi-select
      Object.keys(this.columnFilters).forEach(field => {
        const filterValues = this.columnFilters[field];
        if (filterValues && filterValues.length > 0) {
          data = data.filter(row => filterValues.includes(row[field]));
        }
      });

      // Ajouter un champ de groupe pour toutes les lignes
      return [
        {
          children: [...data]
        }
      ]
    },
    statusCounts() {
      // A faire, facturé, payé, versé
      let counts = [0, 0, 0, 0];

      console.log("effectiveRows changed: ", this.effectiveRows)

      if (!this.effectiveRows || !Array.isArray(this.effectiveRows)) {
        return counts;
      }

      this.effectiveRows.forEach(row => {
        counts[row.MinStatus] += 1;
      });

      return counts;
    },
    tableColumns() {
      // let effectiveRows = this.effectiveRows;

      const columns = [
        {
          label: 'Fournisseur',
          field: 'FrsName',
          headerField: () => "Total",
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par fournisseur',
            filterDropdownItems: [],
            filterFn: this.partialMatch
          }
        },
        {
          label: 'Code PGI',
          field: 'FrsIdCompta',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par code PGI',
            filterDropdownItems: [],
            filterFn: this.partialMatch
          }
        },
        {
          label: 'Famille',
          field: 'N1_Code',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par famille',
            filterDropdownItems: this.uniqueN1Code,
          }
        },
        {
          label: 'GRFA',
          field: 'Grfa',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par GRFA',
            filterDropdownItems: this.uniqueGrfas,
          }
        },
        {
          label: 'Commentaire CDM',
          field: 'CdmComment',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par commentaire',
            filterDropdownItems: [],
            filterFn: this.partialMatch
          }
        },
        {
          label: 'Instance',
          field: 'CountryGathering',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par instance',
            filterDropdownItems: this.uniqueCountryGathering,
          }
        },
        {
          label: 'Sur-fournisseur',
          field: 'IsSurFournisseurText',
          headerField: (group) => group.children.filter(r => r.IsSurFournisseur).length,
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer',
            filterDropdownItems: this.uniqueIsSurFournisseurText
          }
        },
        {
          label: 'Volume',
          field: 'IsVolumeText',
          headerField: (group) => group.children.filter(r => r.IsVolume).length,
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer',
            filterDropdownItems: this.uniqueIsVolumeText
          }
        },
        {
          label: 'L&L',
          field: 'IsLnLText',
          headerField: (group) => group.children.filter(r => r.IsLnL).length,
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par MME',
            filterDropdownItems: this.uniqueIsLnLText
          }
        },
        {
          label: 'WA',
          field: 'IsWAText',
          headerField: (group) => group.children.filter(r => r.IsWA).length,
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par WA',
            filterDropdownItems: this.uniqueIsWAText
          }
        },
        {
          label: 'Statut (glob)',
          field: 'MinStatusText',
          headerField: () => '',
          sortFn: this.sortByMinStatus,
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par statut global'
          }
        },
        {
          label: 'Statut',
          field: 'BarStatuses',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par statut'
          }
        },
        {
          label: 'Type',
          field: 'BarRfaTypes',
          headerField: () => '',
          filterOptions: {
            enabled: true,
            placeholder: 'Filtrer par type'
          }
        },
        {
          label: 'Taux Fixe N-1',
          field: 'TxRfaFixeN1',
          type: 'number',
          headerField: () => '',
          sortable: true
        },
        {
          label: 'RFA',
          field: 'BarRfaTot',
          headerField: (group) => this.formatCurrency(group.children.reduce((sum, r) => sum + (r.BarRfaTot || 0), 0)),
          type: 'number'
        },
        {
          label: 'RFA BAR',
          field: 'BarRfa',
          headerField: (group) => this.formatCurrency(group.children.reduce((sum, r) => sum + (r.BarRfa || 0), 0)),
          type: 'number'
        },
        {
          label: 'Actions',
          field: 'actions',
          headerField: (group) => group.children.length,
          sortable: false
        }
      ];

      return columns;
    },
    detailColumns() {
      return [
        {
          label: 'Fournisseur',
          field: 'FrsName'
        },
        {
          label: 'Code PGI',
          field: 'FrsIdCompta'
        },
        {
          label: 'ID Cegid',
          field: 'BarCegidId'
        },
        {
          label: 'Nom du BAR',
          field: 'BarName'
        },
        {
          label: 'Montant',
          field: 'BarRfa',
          type: 'number'
        },
        {
          label: 'Type',
          field: 'BarRfaTypeText'
        },
        {
          label: 'Création',
          field: 'BarCreationDate'
        },
        {
          label: 'Statut',
          field: 'BarStatusText',
          sortFn: this.sortByBarStatus
        }
      ];
    }
  },
  watch: {
    acomptesPeriods() {
      this.syncAdvancePeriod();
    },
    year() {
      this.syncAdvancePeriod();
      this.initData();
    },
    detailedPeriods() {
      this.syncAdvancePeriod();
    },
    storeGlobalView() {
      this.initData();
    },
    selectedPeriodId() {
      this.initData();
    },
    selectedConcept() {
      this.initData();
    },
    selectedMode() {
      this.initData();
    },
    globalView() {
      this.initData();
    },
    filteredData() {
      this.updateEffectiveRows();
    }
  },
  methods: {
    updateEffectiveRows() {
      Vue.nextTick(() => {
        const table = this.$refs.table;
        if (table && table.filteredRows && table.filteredRows.length > 0 && table.filteredRows[0] && table.filteredRows[0].children) {
          const newRows = table.filteredRows[0].children;

          // Garde-fou: vérifier si les lignes ont réellement changé
          if (this.effectiveRows.length === newRows.length) {
            const hasChanged = newRows.some((newRow, index) => {
              const oldRow = this.effectiveRows[index];
              return !oldRow || oldRow.SupplierGuid !== newRow.SupplierGuid;
            });

            if (!hasChanged) {
              return; // Pas de changement, on évite le rerender
            }
          }

          this.effectiveRows = newRows;
        }
      })
    },
    initData() {
      if (!this.selectedMode) return;
      if (this.selectedMode === "annuelle" && !this.annualPeriod) return;

      const periodId = this.selectedMode === "annuelle" ? this.annualPeriod.id : this.selectedPeriodId;
      const concept = this.selectedMode === "annuelle" ? this.selectedConcept : 1;
      if (!periodId) return;

      this.loading = true;

      getSuiviGrfaDetails(periodId, concept, this.storeGlobalView)
        .then(res => {
          this.data = res;
          this.loading = false;
          this.updateEffectiveRows();
        })
        .catch(err => {
          console.error('Erreur lors du chargement des données:', err);
          this.loading = false;
        });
    },
    showDetails(row) {
      this.selectedRowDetails = row.details;
      this.showDetailsModal = true;
    },
    closeModal() {
      this.showDetailsModal = false;
      this.selectedRowDetails = [];
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD/MM/YYYY');
    },
    partialMatch(data, filterString) {
      const cellValue = data ? data.toString().toLowerCase() : '';
      const filter = filterString ? filterString.toLowerCase() : '';
      return cellValue.includes(filter);
    },
    updateColumnFilters(field, filterData) {
      this.columnFilters = {
        ...this.columnFilters,
        [field]: filterData
      };
    },
    updateStatusFilter(filterData) {
      this.statusFilter = filterData || [];
    },
    updateTypeFilter(filterData) {
      this.typeFilter = filterData || [];
    },
    sortByMinStatus(x, y, col, rowX, rowY) {
      // Tri basé sur MinStatus (numérique)
      return (rowX.MinStatus < rowY.MinStatus ? -1 : (rowX.MinStatus > rowY.MinStatus ? 1 : 0));
    },
    sortByBarStatus(x, y, col, rowX, rowY) {
      // Tri basé sur BarStatus (numérique)
      return (rowX.BarStatus < rowY.BarStatus ? -1 : (rowX.BarStatus > rowY.BarStatus ? 1 : 0));
    },
    syncAdvancePeriod() {
      let period = null;
      const now = dayjs();

      if (this.year < now.year()) {
        // C'est l'année précédente. On retourne le dernier acompte : T3
        period = this.detailedPeriods.find(dp => dp.declarationType == 2 && dp.rfaPaymentFrequency == 4 && dp.month == 10 && dp.year == this.year);
      } else if (this.year > now.year()) {
        // Fin 2025 sélection contrats 2026. On affiche le T3 2025
        period = this.detailedPeriods.find(dp => dp.declarationType == 2 && dp.rfaPaymentFrequency == 4 && dp.month == 10 && dp.year == this.year - 1);
      } else if (this.year == now.year()) {
        let t2Treshold = dayjs().month(6).date(15);
        let t3Treshold = dayjs().month(9).date(15);

        if (now.isAfter(t3Treshold)) {
          // On est après le 15 octobre, T3 disponibles
          period = this.detailedPeriods.find(dp => dp.declarationType == 2 && dp.rfaPaymentFrequency == 4 && dp.month == 10 && dp.year == this.year);
        } else if (now.isAfter(t2Treshold)) {
          // On est après le 15 juillet, T2 disponibles
          period = this.detailedPeriods.find(dp => dp.declarationType == 2 && dp.rfaPaymentFrequency == 4 && dp.month == 7 && dp.year == this.year);
        } else {
          // On affiche T1 par défaut
          period = this.detailedPeriods.find(dp => dp.declarationType == 2 && dp.rfaPaymentFrequency == 4 && dp.month == 4 && dp.year == this.year);
        }
      }

      if (period) {
        this.selectedPeriodId = period.id;
      } else if (this.acomptesPeriods.length > 0) {
        // Fallback si aucune période trouvée avec la logique
        this.selectedPeriodId = this.acomptesPeriods[0].id;
      }
    },
    handleExport() {
      // Définir les colonnes avec format
      const exportColumns = [
        { label: 'Fournisseur', field: 'FrsName' },
        { label: 'Code PGI', field: 'FrsIdCompta' },
        { label: 'Famille', field: 'N1_Code' },
        { label: 'GRFA', field: 'Grfa' },
        { label: 'Commentaire CDM', field: 'CdmComment' },
        { label: 'Instance', field: 'CountryGathering' },
        { label: 'Sur-fournisseur', field: 'IsSurFournisseur' },
        { label: 'Volume', field: 'IsVolume' },
        { label: 'L&L', field: 'IsLnL' },
        { label: 'WA', field: 'IsWA' },
        { label: 'Statut (glob)', field: 'MinStatusText' },
        { label: 'Taux Fixe N-1', field: 'TxRfaFixeN1', format: 'percentage' },
        { label: 'Evolution Taux Fixe N-1', field: 'ProgTxRfaFixeN1', format: 'percentage' },
        { label: 'RFA', field: 'BarRfaTot', format: 'currency' },
        { label: 'ID Cegid', field: 'BarCegidId' },
        { label: 'Nom du BAR', field: 'BarName' },
        { label: 'Statut', field: 'BarStatusText' },
        { label: 'Type', field: 'BarRfaTypeText' },
        { label: 'Création', field: 'BarCreationDate' },
        { label: 'RFA BAR', field: 'BarRfa', format: 'currency' }
      ];

      // Générer les lignes
      const exportRows = [];

      this.effectiveRows.forEach(group => {
        if (group.details && group.details.length > 0) {
          // Si le groupe a des détails, créer une ligne par détail
          group.details.forEach(detail => {
            exportRows.push({
              FrsName: group.FrsName,
              FrsIdCompta: group.FrsIdCompta,
              N1_Code: group.N1_Code,
              Grfa: group.Grfa,
              CdmComment: group.CdmComment,
              CountryGathering: group.CountryGathering,
              IsSurFournisseur: group.IsSurFournisseur ? 'VRAI' : 'FAUX',
              IsVolume: group.IsVolume ? 'VRAI' : 'FAUX',
              IsLnL: group.IsLnL ? 'VRAI' : 'FAUX',
              IsWA: group.IsWA ? 'VRAI' : 'FAUX',
              MinStatusText: group.MinStatusText,
              TxRfaFixeN1: group.TxRfaFixeN1 || 0,
              ProgTxRfaFixeN1: group.ProgTxRfaFixeN1 || 0,
              BarRfaTot: group.BarRfaTot || 0,
              BarCegidId: detail.BarCegidId,
              BarName: detail.BarName,
              BarStatusText: detail.BarStatusText,
              BarRfaTypeText: detail.BarRfaTypeText,
              BarCreationDate: this.formatDate(detail.BarCreationDate),
              BarRfa: detail.BarRfa || 0
            });
          });
        } else {
          // Si le groupe n'a pas de détails, créer une ligne avec juste les infos du groupe
          exportRows.push({
            FrsName: group.FrsName,
            FrsIdCompta: group.FrsIdCompta,
            N1_Code: group.N1_Code,
            Grfa: group.Grfa,
            CdmComment: group.CdmComment,
            CountryGathering: group.CountryGathering,
            IsSurFournisseur: group.IsSurFournisseur ? 'VRAI' : 'FAUX',
            IsVolume: group.IsVolume ? 'VRAI' : 'FAUX',
            IsLnL: group.IsLnL ? 'VRAI' : 'FAUX',
            IsWA: group.IsWA ? 'VRAI' : 'FAUX',
            MinStatusText: group.MinStatusText,
            TxRfaFixeN1: group.TxRfaFixeN1 || 0,
            ProgTxRfaFixeN1: group.ProgTxRfaFixeN1 || 0,
            BarRfaTot: group.BarRfaTot || 0,
            BarCegidId: '',
            BarName: '',
            BarStatusText: '',
            BarRfaTypeText: '',
            BarCreationDate: '',
            BarRfa: 0
          });
        }
      });

      // Générer le nom du fichier
      const modeLabel = this.selectedMode === 'annuelle' ? 'Annuelle' : 'Acomptes';
      const conceptLabel = this.selectedMode === 'annuelle'
        ? (this.selectedConcept === 1 ? 'CMEM' : this.selectedConcept === 2 ? 'MME' : 'WA')
        : '';
      const filename = `Suivi_RFA_${modeLabel}${conceptLabel ? '_' + conceptLabel : ''}`;

      exportExcel(exportColumns, exportRows, filename);
    }
  }
}
</script>

<style>
.data-table.custom-responsive-suivi-rfa .vgt-responsive {
  overflow: auto;
  max-height: calc(100vh - 400px);
  max-width: calc(100vw - 188px);
}

.data-table.custom-responsive-suivi-rfa table.vgt-table {
  font-size: 12px;
}

.data-table.custom-responsive-suivi-rfa table.vgt-table th {
  white-space: nowrap;
  /* Permettre aux petites colonnes (L&L/WA) d'afficher la valeur du filtre sélectionné */
  min-width: 80px !important; /* On écrase le style inline "min-width: auto" appliqué par vue-good-table */
}

.data-table.custom-responsive-suivi-rfa table.vgt-table td {
  padding: 4px;
}

.data-table.custom-responsive-suivi-rfa thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table.custom-responsive-suivi-rfa tbody tr:first-child {
}
</style>

<style scoped>
.suivi-rfa-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.status-tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}

.status-tile {
  padding: 8px 20px;
  text-align: center;
}

.tile-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
}

.tile-value {
  font-size: 32px;
  font-weight: bold;
  color: #333;
}

.suivi-rfa-box {
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
}

.title-with-mode {
  display: flex;
  align-items: center;
  gap: 15px;
}

.title-with-mode h4 {
  margin: 0;
  font-size: 20px;
  border-left: 3px solid #A7C13C;
  padding-left: 8px;
}

.mode-select {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  cursor: pointer;
}

.secondary-filter {
  display: flex;
  align-items: center;
}

.concept-select,
.period-select {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  cursor: pointer;
  min-width: 150px;
}

.btn-export {
  font-size: 14px;
  margin-left: 15px;
}

.btn-export:hover:not(:disabled) {
  background-color: #96af35;
}

.btn-export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading {
  padding: 20px;
  text-align: center;
  font-style: italic;
}

.data-table {
  margin-top: 20px;
  /*max-height: calc(100vh - 300px);
  //max-width: calc(100vw - 230px);*/
}


.no-data {
  margin-top: 20px;
  padding: 20px;
  text-align: center;
  color: #666;
}

.btn-details {
  font-size: 12px;
  padding: 4px 8px;
  background-color: #A7C13C;
  color: black;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.btn-details:hover {
  background-color: #96af35;
}

.trend-indicator {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  font-size: 0.8em;
}

.trend-indicator.up {
  color: #388E3C;
}

.trend-indicator.up::before {
  content: url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%23388E3C%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%3E%3Cpath%20d%3D%22m3%2017l6-6l4%204l8-8%22%2F%3E%3Cpath%20d%3D%22M17%207h4v4%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E');
}

.trend-indicator.down {
  color: #D32F2F;
}

.trend-indicator.down::before {
  content: url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cg%20fill%3D%22none%22%20stroke%3D%22%23D32F2F%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%3E%3Cpath%20d%3D%22m3%207l6%206l4-4l8%208%22%2F%3E%3Cpath%20d%3D%22M17%2017h4v-4%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E');
}

.total-row {
  background-color: #f5f5f5;
  font-weight: bold;
  border-top: 2px solid #A7C13C;
  border-bottom: 2px solid #A7C13C;
}

.total-row td {
  padding: 12px 8px;
  text-align: center;
}

.total-row-bottom {
  width: 100%;
  margin-top: 10px;
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 10;
}

.total-row-bottom table {
  width: 100%;
  border-collapse: collapse;
  background-color: #f5f5f5;
  border-top: 2px solid #A7C13C;
  border-bottom: 2px solid #A7C13C;
}

.total-row-bottom table tr td {
  padding: 12px 8px;
  text-align: center;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/pages/RFA/DashboardGRFA/Details/SuiviRfa.vue